import { useEffect, useState } from "react";
import axiosInstance from "../axios/axios";
import postIcon from "../assets/postIcon.svg";
import ErrorMessage from "./ErrorMessage";
import { useNavigate } from "react-router-dom";
import SuccessMessage from "./SuccessMessage";

const CreateJob = () => {
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [location, setLocation] = useState("");
  const [message, setMessage] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [stipend, setStipend] = useState("");
  const [workEnvironment, setWorkEnvironment] = useState("");
  const [deadline, setDeadline] = useState("");
  const [amount, setAmount] = useState("");
  const [field, setField] = useState("");
  const [duration, setDuration] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [requirements, setRequirements] = useState("");
  const [orgId, setOrgId] = useState("");
  const [loading, setLoading] = useState(false);

  // Additional Questions State
  const [showQuestions, setShowQuestions] = useState(false);
  const [q1, setQ1] = useState("");
  const [q2, setQ2] = useState("");
  const [q3, setQ3] = useState("");
  const [q4, setQ4] = useState("");
  const [q5, setQ5] = useState("");

  const user_id = localStorage.getItem("user_id");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrgDetails = async () => {
      try {
        const res = await axiosInstance.get("all_users");
        const foundUser = res.data.users.find(
          (u) => u.user_id === parseInt(user_id)
        );
        setCompanyName(foundUser.organization.company_name);
        setLocation(foundUser.organization.company_state);
        setOrgId(foundUser.organization_id);
      } catch (error) {
        console.log(error);
      }
    };

    fetchOrgDetails();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const stipendValue = stipend === "true" ? 1 : stipend === "false" ? 0 : null;
    const workEnvValue = workEnvironment || null;

    // Prepare the job object to be posted
    const newJob = {
      org_id: orgId,
      job_title: jobTitle,
      field,
      department,
      stipend: stipendValue,
      env_mode: workEnvValue,
      amount,
      duration,
      location,
      deadline,
      job_description: jobDescription,
      job_requirements: requirements,
      q1,
      q2,
      q3,
      q4,
      q5,
    };

    try {
      const response = await axiosInstance.post("jobs", newJob);
      setMessage("Job Created Successfully!");
      navigate("/dashboard/jobs");
    } catch (error) {
      setError("Failed to create job. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 sm:px-6 py-4">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
            Create Job Posting
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Fill in the details to create a new job opportunity
          </p>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-6">
        {/* Error and Success Messages */}
        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} />
          </div>
        )}
        {message && (
          <div className="mb-6">
            <SuccessMessage message={message} />
          </div>
        )}

        {/* Form Container */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Company Information Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Company Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={companyName}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Location
                  </label>
                  <input
                    type="text"
                    value={location}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>
            </div>

            {/* Job Details Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Job Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="e.g. Software Developer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="e.g. Engineering"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Field <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={field}
                    onChange={(e) => setField(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Select Field</option>
                    <option value="Art">Art</option>
                    <option value="Science">Science</option>
                    <option value="Commercial">Commercial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="e.g. 6 months"
                  />
                </div>
              </div>
            </div>

            {/* Work Environment & Compensation Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Work Environment & Compensation
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Work Environment <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={workEnvironment}
                    onChange={(e) => setWorkEnvironment(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Select Environment</option>
                    <option value="onsite">Onsite</option>
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stipend <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={stipend}
                    onChange={(e) => setStipend(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Select Option</option>
                    <option value="false">No Stipend</option>
                    <option value="true">Paid Stipend</option>
                  </select>
                </div>
                {stipend === "true" && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stipend Amount - Per month <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="e.g. 50,000 - 80,000"
                    />
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Application Deadline <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={deadline}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setDeadline(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>
            </div>

            {/* Job Description Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Job Description & Requirements
              </h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    rows={5}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Describe the role, responsibilities, and what the intern will learn..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Requirements & Qualifications
                  </label>
                  <textarea
                    value={requirements}
                    onChange={(e) => setRequirements(e.target.value)}
                    rows={5}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="• Bachelor's degree in relevant field&#10;• Proficient in React and JavaScript&#10;• Strong communication skills&#10;• Available for full-time internship"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Use bullet points (•) to list requirements clearly
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Questions Section */}
            <div>
              <button
                type="button"
                className={`mb-4 px-4 py-2 rounded-lg border ${
                  showQuestions
                    ? "bg-green-50 border-green-500 text-green-700"
                    : "bg-gray-100 border-gray-300 text-gray-700"
                } font-medium transition-colors`}
                onClick={() => setShowQuestions((prev) => !prev)}
              >
                {showQuestions ? "Hide Additional Questions" : "Include Additional Questions"}
              </button>
              {showQuestions && (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-gray-900 mb-2">
                    Additional Questions for Candidates (Optional)
                  </h3>
                  <input
                    type="text"
                    value={q1}
                    onChange={(e) => setQ1(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Question 1"
                  />
                  <input
                    type="text"
                    value={q2}
                    onChange={(e) => setQ2(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Question 2"
                  />
                  <input
                    type="text"
                    value={q3}
                    onChange={(e) => setQ3(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Question 3"
                  />
                  <input
                    type="text"
                    value={q4}
                    onChange={(e) => setQ4(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Question 4"
                  />
                  <input
                    type="text"
                    value={q5}
                    onChange={(e) => setQ5(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Question 5"
                  />
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={loading}
                className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 text-white text-sm font-medium rounded-lg transition-colors duration-200 ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                <img src={postIcon} alt="" className="w-5 h-5" />
                <span>{loading ? "Creating Job..." : "Post Job"}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateJob;